import "../web/assets/css/site.css";
import { fetchMarkdownFiles } from "./content/github";
import { buildContentStore } from "./content/store";
import type { ContentStore, RuntimeConfig } from "./content/types";
import { attachArchiveSearch, renderArchive, renderArticle, renderError, renderLayout, renderLoading, renderNotFound, renderSeries } from "./ui/render";

const app = document.querySelector<HTMLDivElement>("#app");
let config: RuntimeConfig | null = null;
let storePromise: Promise<ContentStore> | null = null;

async function bootstrap(): Promise<void> {
  if (!app) {
    throw new Error("missing #app root");
  }
  config = await loadConfig();
  app.innerHTML = renderLoading(config);
  await renderRoute();
  window.addEventListener("popstate", () => void renderRoute());
  document.addEventListener("click", (event) => {
    const link = (event.target as Element | null)?.closest<HTMLAnchorElement>("a[data-link]");
    if (!link || link.origin !== window.location.origin || link.target) {
      return;
    }
    event.preventDefault();
    window.history.pushState(null, "", link.href);
    void renderRoute();
  });
}

async function renderRoute(): Promise<void> {
  if (!app || !config) {
    return;
  }
  try {
    const store = await getStore(config);
    const path = window.location.pathname.replace(/\/+$/, "") || "/";
    if (path === "/" || path === "/articles") {
      app.innerHTML = renderArchive(config, store);
      attachArchiveSearch();
      return;
    }
    const articleMatch = path.match(/^\/articles\/([^/]+)$/);
    if (articleMatch) {
      const article = store.articles.get(decodeURIComponent(articleMatch[1]));
      app.innerHTML = article ? renderArticle(config, article) : renderNotFound(config);
      return;
    }
    const seriesMatch = path.match(/^\/series\/([^/]+)$/);
    if (seriesMatch) {
      const series = store.series.get(decodeURIComponent(seriesMatch[1]));
      app.innerHTML = series ? renderSeries(config, series) : renderNotFound(config);
      return;
    }
    app.innerHTML = renderNotFound(config);
  } catch (error) {
    app.innerHTML = renderError(config, error);
  }
}

async function getStore(runtimeConfig: RuntimeConfig): Promise<ContentStore> {
  if (!storePromise) {
    storePromise = fetchMarkdownFiles(runtimeConfig).then((files) => buildContentStore(files, runtimeConfig.github.contentPath));
  }
  return storePromise;
}

async function loadConfig(): Promise<RuntimeConfig> {
  const response = await fetch("/spacetree.config.json", { cache: "no-store" });
  if (!response.ok) {
    throw new Error(`config request failed: ${response.status}`);
  }
  return response.json();
}

bootstrap().catch((error) => {
  if (app) {
    const fallback: RuntimeConfig = {
      siteTitle: "SpaceTree.blog",
      github: { owner: "", repo: "", ref: "", contentPath: "" },
    };
    app.innerHTML = renderLayout(fallback, `<p class="empty app-state">Unable to start application: ${String(error)}</p>`);
  }
});
